import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Camera } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "Available Lots", id: "lots" },
  { label: "About Woodlake", id: "about" },
  { label: "Contact", id: "contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled ? "bg-cream/90 backdrop-blur-xl border-border shadow-sm" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => go("home")}
          className={`font-serif text-2xl tracking-tight transition-colors duration-500 ${scrolled ? "text-forest" : "text-cream"}`}
        >
          Build <span className="italic text-brass">in</span> Woodlake
        </button>
        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className={`font-sans text-sm tracking-wide transition-colors duration-300 hover:text-brass ${
                scrolled ? "text-forest" : "text-cream/90"
              }`}
            >
              {l.label}
            </button>
          ))}
          <Link
            data-testid="nav-gallery"
            to="/gallery"
            className={`flex items-center gap-1.5 font-sans text-sm tracking-wide transition-colors duration-300 hover:text-brass ${
              scrolled ? "text-forest" : "text-cream/90"
            }`}
          >
            <Camera size={15} /> Gallery
          </Link>
          <button
            data-testid="header-view-lots-btn"
            onClick={() => go("lots")}
            className="bg-brass text-forest font-sans text-sm font-semibold tracking-wide px-6 py-2.5 rounded-sm transition-colors duration-300 hover:bg-[#cbaa66]"
          >
            View Available Lots
          </button>
        </nav>
        <button
          data-testid="mobile-menu-btn"
          className={`lg:hidden transition-colors duration-500 ${scrolled ? "text-forest" : "text-cream"}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-cream border-b border-border overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-2xl text-forest"
                >
                  {l.label}
                </button>
              ))}
              <Link data-testid="mobile-nav-gallery" to="/gallery" className="text-left font-serif text-2xl text-forest flex items-center gap-3">
                <Camera size={22} className="text-brass" /> Gallery
              </Link>
              <button
                data-testid="mobile-view-lots-btn"
                onClick={() => go("lots")}
                className="bg-brass text-forest font-sans text-sm font-semibold px-6 py-3 rounded-sm w-fit"
              >
                View Available Lots
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
